import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resolveApiUrl } from '../../apiBase';
import { felmeddelandeDelningslank } from '../../utils/felmeddelanden.js';

async function fetchExternal(token, path) {
  const apiBase = resolveApiUrl();
  const url = `${apiBase}/tenants/external/ice/${encodeURIComponent(token)}${path}`;
  const resp = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });
  if (!resp.ok) {
    // Mottagaren ar utomstaende: statuskoder och backend-detaljer
    // sager dem ingenting. Se felmeddelandeDelningslank.
    throw new Error(felmeddelandeDelningslank(resp.status));
  }

  const cloned = resp.clone();
  try {
    return await resp.json();
  } catch {
    const text = await cloned.text();
    const preview = String(text || '').slice(0, 200);
    throw new Error(`Expected JSON from API. Got: ${preview}`);
  }
}

function GoalCard({ goal, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(goal)}
      className={`w-full text-left rounded border p-3 ${selected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
    >
      <div className="text-sm font-semibold">{goal.code || 'Mål'}</div>
      <div className="text-sm text-slate-700">{goal.title}</div>
    </button>
  );
}

function StatusDot({ color }) {
  const c = String(color || '').toLowerCase();
  const cls =
    c === 'red'
      ? 'bg-red-500'
      : c === 'yellow'
        ? 'bg-amber-400'
        : c === 'green'
          ? 'bg-emerald-500'
          : 'bg-slate-300';
  const label = c ? c.toUpperCase() : '—';
  return (
    <span className="inline-flex items-center gap-2" title={label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${cls}`} />
    </span>
  );
}

function KpiRow({ kpi, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(kpi)}
      className="w-full text-left rounded border border-slate-200 bg-white hover:bg-slate-50 p-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">{kpi.name}</div>
        <StatusDot color={kpi.status_color} />
      </div>
      <div className="text-xs text-slate-600">
        {kpi.unit ? `Enhet: ${kpi.unit}` : 'Enhet: -'}
        {kpi.frequency ? ` • Frekvens: ${kpi.frequency}` : ''}
        {kpi.target_value !== null && kpi.target_value !== undefined ? ` • Mål: ${kpi.target_value}` : ''}
        {kpi.latest_value !== null && kpi.latest_value !== undefined ? ` • Senast: ${kpi.latest_value}` : ''}
      </div>
    </button>
  );
}

function Measurements({ token, kpiId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      setError('');
      try {
        const data = await fetchExternal(token, `/measurements?kpi_id=${encodeURIComponent(kpiId)}`);
        if (!alive) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }
    if (token && kpiId) run();
    return () => {
      alive = false;
    };
  }, [token, kpiId]);

  if (!kpiId) return null;
  if (loading) return <div className="text-sm text-slate-600">Laddar mätvärden…</div>;
  if (error) return <div className="text-sm text-red-700">{error}</div>;

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <div className="text-sm text-slate-600">Inga mätvärden.</div>
      ) : (
        items.map((m) => (
          <div key={m.id} className="rounded border border-slate-200 bg-white p-3">
            <div className="text-sm font-semibold">{m.value}</div>
            <div className="text-xs text-slate-600">
              {m.measured_at ? `Datum: ${m.measured_at}` : 'Datum: -'}
              {m.note ? ` • ${m.note}` : ''}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Actions({ token, kpiId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function run() {
      setLoading(true);
      setError('');
      try {
        const data = await fetchExternal(token, `/actions?kpi_id=${encodeURIComponent(kpiId)}`);
        if (!alive) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }
    if (token && kpiId) run();
    return () => {
      alive = false;
    };
  }, [token, kpiId]);

  if (!kpiId) return null;
  if (loading) return <div className="text-sm text-slate-600">Laddar åtgärder…</div>;
  if (error) return <div className="text-sm text-red-700">{error}</div>;

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <div className="text-sm text-slate-600">Inga publicerade åtgärder.</div>
      ) : (
        items.map((a) => (
          <div key={a.id} className="rounded border border-slate-200 bg-white p-3">
            <div className="text-sm font-semibold">{a.title}</div>
            <div className="text-xs text-slate-600">
              {a.state ? `Status: ${a.state}` : ''}
              {a.due_at ? ` • Förfaller: ${a.due_at}` : ''}
              {a.owner_role ? ` • Ansvar: ${a.owner_role}` : ''}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default function ExternalIceView() {
  const { token } = useParams();
  const [goals, setGoals] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const g = await fetchExternal(token, '/goals');
        if (!alive) return;
        const items = Array.isArray(g?.items) ? g.items : [];
        setGoals(items);
        setSelectedGoal(items[0] || null);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }
    if (token) load();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    let alive = true;
    async function loadKpis() {
      setError('');
      setKpis([]);
      setSelectedKpi(null);
      if (!token || !selectedGoal?.id) return;
      try {
        const data = await fetchExternal(token, `/kpis?goal_id=${encodeURIComponent(selectedGoal.id)}`);
        if (!alive) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setKpis(items);
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      }
    }
    loadKpis();
    return () => {
      alive = false;
    };
  }, [token, selectedGoal?.id]);

  const title = useMemo(() => {
    if (!selectedGoal) return 'ICE';
    return `ICE – ${selectedGoal.code || 'Mål'}: ${selectedGoal.title}`;
  }, [selectedGoal]);

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{title}</h1>
        <div className="text-sm text-slate-600">Extern vy (endast publicerat).</div>
      </div>

      {loading ? <div className="text-sm text-slate-600">Laddar…</div> : null}
      {error ? <div className="text-sm text-red-700">{error}</div> : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <div className="text-sm font-semibold">Mål</div>
          {goals.length === 0 ? (
            <div className="text-sm text-slate-600">Inga publicerade mål.</div>
          ) : (
            goals.map((g) => (
              <GoalCard
                key={g.id}
                goal={g}
                selected={selectedGoal?.id === g.id}
                onSelect={(goal) => {
                  setSelectedGoal(goal);
                  setSelectedKpi(null);
                }}
              />
            ))
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold">KPI:er</div>
          {selectedGoal ? (
            kpis.length === 0 ? (
              <div className="text-sm text-slate-600">Inga publicerade KPI:er för valt mål.</div>
            ) : (
              <div className="space-y-2">
                {kpis.map((k) => (
                  <KpiRow key={k.id} kpi={k} onSelect={setSelectedKpi} />
                ))}
              </div>
            )
          ) : (
            <div className="text-sm text-slate-600">Välj ett mål.</div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold">Mätvärden</div>
          {selectedKpi ? (
            <>
              <Measurements token={token} kpiId={selectedKpi.id} />
              <div className="pt-3">
                <div className="text-sm font-semibold">Åtgärder</div>
                <div className="mt-2">
                  <Actions token={token} kpiId={selectedKpi.id} />
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-slate-600">Välj en KPI för att se mätvärden.</div>
          )}
        </div>
      </div>
    </div>
  );
}
